import React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    IconButton,
    Divider
} from "@mui/material";
import { styled } from "@mui/system";
import adminEditIcon from "../../../../assets/images/edit.svg";
import adminDeleteIcon from "../../../../assets/images/adminDeleteIcon.svg";
import adminDeleteGrey from "../../../../assets/images/trash_full.svg";
import styles from "./user.module.css";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "#2036C7",
        color: "#fff",
        textAlign: "center",
        padding: "3px",
        fontSize: "12px",
        fontWeight: 400,
        border: "1px solid #EAECF0",
        position: "sticky",
        top: 0,
        zIndex: 10,
    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: 12,
        fontWeight: 400,
        padding: "3px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
        textAlign: "center",
    },
}));

const StyledTableRow = styled(TableRow)({});

const UserTable = ({ data = [], scopeOptionMeta = {}, selectedUsers, onUserSelection, onEditUser, onDeleteUser }) => {
    const parseScopeValueForDisplay = (scopeValue) => {
        if (Array.isArray(scopeValue)) {
            return scopeValue
                .map(value => (value === null || value === undefined ? '' : String(value).trim()))
                .filter(Boolean);
        }

        if (scopeValue === null || scopeValue === undefined || scopeValue === '') {
            return [];
        }

        return String(scopeValue)
            .split(',')
            .map(value => value.replaceAll('[', '').replaceAll(']', '').replace(/['"`]/g, '').trim())
            .filter(Boolean);
    };

    const getOptionComparableValue = (option) => {
        if (option && typeof option === 'object') {
            return option.value ?? option.label ?? option.name ?? option.id ?? null;
        }

        return option;
    };

    const getUniqueOptionCount = (options = []) => {
        const normalizedValues = options
            .map(getOptionComparableValue)
            .map(value => (value === null || value === undefined ? '' : String(value).trim()))
            .filter(Boolean);

        return new Set(normalizedValues).size;
    };

    const bgOptionCount = getUniqueOptionCount(scopeOptionMeta?.bgOptions || []);
    const siteClusterOptionCount = getUniqueOptionCount(scopeOptionMeta?.siteClusterOptions || []);

    const getScopeValueDisplayText = (row) => {
        const scope = String(row?.Scope || '').trim().toLowerCase();
        const parsedScopeValues = parseScopeValueForDisplay(row?.['Scope Value']);
        const selectedCount = new Set(parsedScopeValues).size;

        if (scope === 'bg' && bgOptionCount > 0 && selectedCount === bgOptionCount) {
            return "All BG's";
        }

        if (scope === 'site cluster' && siteClusterOptionCount > 0 && selectedCount === siteClusterOptionCount) {
            return "All Site Cluster's";
        }

        if (Array.isArray(row?.['Scope Value'])) {
            return row['Scope Value'].join(', ');
        }

        return row?.['Scope Value'];
    };

    const getDisplayValue = (row, col) => {
        if (col === 'Scope Value') {
            return getScopeValueDisplayText(row);
        }

        return row[col];
    };

    const transformedData = data.map(item => ({
        Name: item.Name || item.username,
        Persona: item.Persona || item.persona,
        "Email ID": item["Email ID"] || item.user_email_id,
        "User type": item["User Type"] || item.user_type,
        "Access Type": item["Access Type"] || item.access_type,
        "Scope": item["Scope"] || item.Scope,
        "Scope Value": item["Scope Value"] || item["Scope Value"],
        originalData: item
    }));

    const visibleColumns = ["Name", "Persona", "Email ID", "User type", "Access Type", "Scope", "Scope Value"];
    const allUserNames = transformedData.map(row => row.Name);

    const handleSelectAll = (event) => {
        const newSelection = event.target.checked ? allUserNames : [];
        onUserSelection?.(newSelection);
    };

    const handleSelectSingle = (userName, isChecked) => {
        let newSelection;
        if (isChecked) {
            newSelection = [...selectedUsers, userName];
        } else {
            newSelection = selectedUsers.filter(name => name !== userName);
        }
        onUserSelection?.(newSelection);
    };

    const isAllSelected = transformedData.length > 0 && selectedUsers.length === transformedData.length;
    const isSomeSelected = selectedUsers.length > 0 && selectedUsers.length < transformedData.length;

    return (
        <TableContainer className={styles.table} style={{
            maxHeight: "480px",
            overflowY: "auto",
            maxWidth: "100%",
            overflowX: "auto",
        }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <StyledTableCell padding="checkbox">
                            <Checkbox
                                size="small"
                                checked={isAllSelected}
                                indeterminate={isSomeSelected}
                                style={{ color: "#FFFFFF" }}
                                onChange={handleSelectAll}
                            />
                        </StyledTableCell>

                        {visibleColumns.map((col, index) => (
                            <StyledTableCell key={index}>{col}</StyledTableCell>
                        ))}
                        <StyledTableCell>Action</StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {transformedData.length > 0 ? (
                        transformedData.map((row, rowIndex) => (
                            <StyledTableRow key={rowIndex + Math.random()}>
                                <StyledTableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedUsers.includes(row.Name)}
                                        onChange={(e) => handleSelectSingle(row.Name, e.target.checked)}
                                        size="small"
                                    />
                                </StyledTableCell>
                                {visibleColumns.map((col, colIndex) => (
                                    <StyledTableCell key={colIndex}>
                                        {getDisplayValue(row, col) !== null && getDisplayValue(row, col) !== undefined
                                            ? getDisplayValue(row, col)
                                            : ""}
                                    </StyledTableCell>
                                ))}

                                <StyledTableCell style={{ display: "flex", justifyContent: "space-evenly" }}>
                                    <IconButton 
                                        size="small" 
                                        color="primary"
                                        onClick={() => onEditUser?.(row.originalData)}
                                    >
                                        <img src={adminEditIcon} alt="editicon" width={16} height={16} />
                                    </IconButton>
                                    <Divider orientation="vertical" flexItem sx={{ mx: 1, bgcolor: "#DBDBDB" }} />
                                    <IconButton 
                                        size="small"
                                        onClick={() => {
                                            if (selectedUsers.includes(row.Name) && onDeleteUser) {
                                                onDeleteUser([row.Name]);
                                            }
                                        }}
                                    >
                                        <img
                                            src={selectedUsers.includes(row.Name) ? adminDeleteIcon : adminDeleteGrey}
                                            alt="deleteIcon"
                                            width={18}
                                            height={18}
                                            style={{
                                                cursor: selectedUsers.includes(row.Name) ? "pointer" : "not-allowed",
                                            }}
                                        />
                                    </IconButton>
                                </StyledTableCell>
                            </StyledTableRow>
                        ))
                    ) : (
                        <StyledTableRow>
                            <StyledTableCell
                                colSpan={visibleColumns.length + 2}
                                align="center"
                                sx={{
                                    height: "80px",
                                    border: "none"
                                }}
                            >
                                Data not available...
                            </StyledTableCell>
                        </StyledTableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default UserTable;